import Vue from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';

import Toast from "vue-toastification";
import BootstrapVue from 'bootstrap-vue';
import ToggleButton from 'vue-js-toggle-button';
import VueSwal from 'vue-swal';
import VueLogger from 'vuejs-logger';
import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
//@ts-ignore
import { throttle, debounce } from 'throttle-debounce';
import featureFlags from "@/util/featureFlags";

Vue.use(ToggleButton);
Vue.use(BootstrapVue);
Vue.use(VueSwal);

const options = {
  isEnabled: true,
  logLevel : process.env.VUE_APP_LOG_LEVEL || "warn",
  stringifyArguments : false,
  showLogLevel : true,
  showMethodName : true,
  separator: '|',
  showConsoleColors: true,
};

Vue.use(VueLogger, options);

Vue.config.productionTip = false;

Sentry.init({
    enabled: process.env.ENABLE_SENTRY === "yes",
    dsn: 'https://e4e29bc265574787b01d50cf6d1eaf9f@sentry.uniki.de/5',
    integrations: [new Integrations.Vue({Vue, attachProps: true})],
});

const toastOptions = {
  position: 'top-right',
  pauseOnFocusLoss: false,
  timeout: 7500,
};

Vue.use(Toast, toastOptions);

//First: detect if we need to redirect, if yes: do so, otherwise let vue load:
const urlParams = new URLSearchParams(window.location.search);
if (window.location.pathname === "/redirect" && urlParams.has("path")) {
  //Extract the first parameter:
  const target = urlParams.get("path");
  urlParams.delete("path");
  const targetLocation = window.location.origin + target + "?" + urlParams.toString();

  if (window.self !== window.top) {
    // The script is running inside an iframe
    window.top.location.href = targetLocation;
  } else {
    // The script is running in the top window
    window.location.href = targetLocation;
  }
}


//Load session data and feature flags:
const preparations: Array<Promise<void>> = [];
preparations.push(store.dispatch("tryRestoreLogin"));
preparations.push(featureFlags.fetchFromServer());

Promise.all(preparations).then(() => {
  new Vue({
    router,
    store,
    render: (h) => h(App),
  }).$mount('#app');

  if (featureFlags.enableTracking) {
    // SPA contract: flags must be set before assets.js parses, otherwise it auto-inits
    // without registering the virtualNavigationEvent listener.
    //@ts-ignore
    window.useVirtualNavigationEvents = true;
    //@ts-ignore
    window.runAssetsManually = true;
    // Preserve previous acceptAll() behavior (analytics + marketing) without showing a banner.
    //@ts-ignore
    window.forceAcceptAllConsent = true;

    const tag = document.createElement("script");
    tag.src = "https://uniki.de/js/assets.js";
    document.getElementsByTagName("head")[0].appendChild(tag);

    /** Collapse letterbox/download IDs so analytics aggregates like assets.js intended. */
    const analyticsPath = (path: string): string => {
      if (/^\/letterbox\/[^/]+\/download\/?$/.test(path)) return "/letterbox/download";
      if (/^\/letterbox\/[^/]+\/upload\/?$/.test(path)) return "/letterbox/upload";
      if (/^\/download\/[^/]+\/?$/.test(path)) return "/download";
      if (/^\/resetpassword\/[^/]+\/?$/.test(path)) return "/resetpassword/:nonce";
      if (/^\/redeem\/[^/]+\/[^/]+\/?$/.test(path)) return "/redeem/:name/:token";
      return path;
    };

    let trackingStarted = false;
    const trackRoute = (path: string) => {
      //@ts-ignore
      if (!window.startAssets) return;
      //@ts-ignore
      window.startAssets();
      document.dispatchEvent(new CustomEvent("virtualNavigationEvent", {
        detail: analyticsPath(path),
      }));
    };

    const startTracking = () => {
      if (trackingStarted) return;
      //@ts-ignore
      if (!window.startAssets) return;
      trackingStarted = true;
      // Idempotent startAssets + pageview on every Vue route change.
      router.afterEach((to) => {
        trackRoute(to.path);
      });
      // Cover the route that was already active when the script finished loading.
      trackRoute(router.currentRoute.path);
    };

    tag.addEventListener("load", () => {
      window.setTimeout(startTracking, 0);
    });
    // Fallback if the load event was missed (cached script) or startAssets is delayed.
    let attempts = 0;
    const poll = window.setInterval(() => {
      attempts++;
      //@ts-ignore
      if (window.startAssets) {
        window.clearInterval(poll);
        startTracking();
      } else if (attempts >= 50) {
        window.clearInterval(poll);
        Vue.$log.warn("startAssets not available after loading assets.js");
      }
    }, 100);
  }
});

if (store.state.serviceWorkerIsSupported) {
  navigator.serviceWorker.register('/sw.js', {updateViaCache: "none"}).then((registration) => {
    Vue.$log.debug('SW registered: ', registration);
    store.commit("setServiceWorkerRegistered");
  }).catch((registrationError) => {
    Vue.$log.fatal("Worker registration failed, falling back to normal download", registrationError);
  });

  //Debugging handler:
  navigator.serviceWorker.addEventListener("message", (evt) => {
    Vue.$log.debug('Msg from SW: ', evt.data);
  });
} else {
  Vue.$log.warn("Service workers not supported in this Browser, falling back to normal download");
  // Service worker is not supported
}
